/**
 * Composant d'upload de photo pour un livreur
 * Drag & drop + sélection de fichier
 */

import { useState } from 'react';
import { X, Camera, Loader2 } from 'lucide-react';
import { useLivreurPhotoUpload } from '../utils/useLivreurPhotoUpload';

const LivreurPhotoUpload = ({
  photoUrl,
  onPhotoChange,
  commercantId = null,
  folderBase = 'livreurs',
  label = 'Photo',
  imageAlt = 'Photo livreur',
  disabled = false,
}) => {
  const [dragActive, setDragActive] = useState(false);

  const { uploading, error, fileInputRef, handleFileInput, openFilePicker, clearPhoto, handleFile } =
    useLivreurPhotoUpload({
      onPhotoChange,
      commercantId,
      folderBase,
      disabled,
    });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>

      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : photoUrl
            ? 'border-gray-300'
            : 'border-gray-300 hover:border-gray-400'
        } ${uploading || disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFilePicker}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          className="hidden"
          disabled={uploading || disabled}
        />

        {uploading ? (
          <div className="flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-2" />
            <p className="text-sm text-gray-600">Upload en cours...</p>
          </div>
        ) : photoUrl ? (
          <div className="flex items-center gap-4">
            <img
              src={photoUrl}
              alt={imageAlt}
              className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Photo téléchargée</p>
              <p className="text-xs text-gray-500">Cliquez pour changer ou glissez-déposez une nouvelle image</p>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearPhoto();
              }}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Supprimer la photo"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <Camera className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-700 mb-1">
              Cliquez pour télécharger ou glissez-déposez
            </p>
            <p className="text-xs text-gray-500">
              PNG, JPG, WEBP jusqu'à 5 MB
            </p>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default LivreurPhotoUpload;
